import type { LucideIcon } from 'lucide-react';
import type { ChangeEvent, ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { FileUp, ImagePlus, Video } from 'lucide-react';
import { useRef } from 'react';

import type { ButtonProps } from '@/components/ui/button/Button';

import { Button } from '@/components/ui/button/Button';

export type FileTypeConfig = {
  accept: string;
  icon?: LucideIcon;
  label?: string;
};

export interface FileInputProps {
  className?: string;
  onAddFiles: (e: ChangeEvent<HTMLInputElement>) => void;
  children?: ReactNode;
  multiple?: boolean;
  maxFileSize?: number;
  maxFiles?: number;
  showImageButton?: boolean;
  showVideoButton?: boolean;
  showAllFilesButton?: boolean;
  buttonSize?: ButtonProps['size'];
  buttonVariant?: ButtonProps['variant'];
  gridClassName?: string;
  imageTypes?: string;
  videoAndAudioTypes?: string;
  customFileTypes?: FileTypeConfig[];
  onError?: (error: string) => void;
}

export function FileInput(fileInputProps: FileInputProps) {
  const {
    onAddFiles,
    children,
    className,
    multiple = true,
    maxFileSize,
    maxFiles,
    showImageButton = true,
    showVideoButton = true,
    showAllFilesButton = true,
    buttonSize = 'sm',
    buttonVariant = 'tertiary',
    gridClassName,
    imageTypes = 'image/jpeg, image/png, image/gif, image/svg+xml, image/webp',
    videoAndAudioTypes = 'video/mp4, video/webm, video/3gp, video/ogg, video/avi, video/mpeg, audio/mpeg, audio/ogg, audio/3gp, audio/mp4, audio/webm, audio/flac, audio/object',
    customFileTypes = [],
    onError,
  } = fileInputProps;

  const attachmentsInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files) {
      if (maxFiles && files.length > maxFiles) {
        onError?.(`Maximum ${maxFiles} files allowed`);
        e.target.value = '';
        return;
      }

      if (maxFileSize) {
        for (let i = 0; i < files.length; i++) {
          const file = files.item(i);
          if (!file) continue;
          if (file.size > maxFileSize) {
            onError?.(`File "${file.name}" exceeds maximum file size of ${maxFileSize / 1024 / 1024}MB`);
            e.target.value = '';
            return;
          }
        }
      }
    }

    onAddFiles(e);
  };

  const triggerFileInput = (acceptValue: string) => {
    if (attachmentsInputRef.current) {
      attachmentsInputRef.current.accept = acceptValue;
      attachmentsInputRef.current.click();
    }
  };

  const imagesTrigger = () => triggerFileInput(imageTypes);
  const videoTrigger = () => triggerFileInput(videoAndAudioTypes);
  const allFilesTrigger = () => triggerFileInput('*');

  return (
    <fieldset className={cn('border-border-primary relative flex w-full flex-col gap-3 rounded-lg border p-3', className)}>
      <input ref={attachmentsInputRef} onChange={handleFileChange} multiple={multiple} className="hidden" type="file" />
      <div className="flex justify-between gap-2">
        <div className="flex w-full justify-end gap-2">
          {showImageButton && (
            <Button type="button" size={buttonSize} variant={buttonVariant} onClick={imagesTrigger}>
              <span className="sr-only">Add images</span>
              <ImagePlus />
            </Button>
          )}

          {showVideoButton && (
            <Button type="button" size={buttonSize} variant={buttonVariant} onClick={videoTrigger}>
              <span className="sr-only">Add videos or audio</span>
              <Video />
            </Button>
          )}

          {showAllFilesButton && (
            <Button type="button" size={buttonSize} variant={buttonVariant} onClick={allFilesTrigger}>
              <span className="sr-only">Add any type of files</span>
              <FileUp />
            </Button>
          )}

          {customFileTypes.map((fileType, index) => {
            const Icon = fileType.icon || FileUp;
            return (
              <Button key={`custom-file-type-${index}`} type="button" size={buttonSize} variant={buttonVariant} onClick={() => triggerFileInput(fileType.accept)}>
                {fileType.label && <span className="sr-only">{fileType.label}</span>}
                <Icon />
              </Button>
            );
          })}
        </div>
      </div>
      <div className={cn('grid w-full grid-cols-[repeat(auto-fit,minmax(6rem,10rem))] grid-rows-[auto] gap-2 empty:hidden', gridClassName)}>{children}</div>
    </fieldset>
  );
}
